// Toolbar buttons for the extensions that are on (shared/ext-pins.js says which, in what order and
// how many fit). Page tools get one by themselves; the rest only when pinned. Extras go in the
// extensions menu (app/ext-ui.js). On phones they all sit in the More tray, with their names.
//
// Right-click, long-press, Shift+F10 or the Menu key opens a small menu on a button: take it off the
// toolbar, move it left or right (so nothing needs dragging), turn it off, or open its settings.
// The group is one Tab stop; arrow keys move inside it.
import { get, set, setMany, subscribe } from "../shared/settings-store.js";
import { EXTENSION_MAP } from "../shared/extensions.js";
import { toolbarExts, maxButtons, pinPatch, movePatch } from "../shared/ext-pins.js";
import { openSettings } from "./tabs.js";
import { announce } from "./announce.js";
import { openDevtoolsActive, clearHighlightsActive, pipActive, pipOn, penOn, setPen, darkPausedNow, setDarkPaused } from "./ext-frames.js";
import { readGate } from "./read-gate.js";
import { musicCtl } from "./music.js";
import { bellNext } from "./bell.js";
import { active } from "./state.js";
import { citeOk, cardOk, citePage, makeCard } from "./card-gate.js";
import { longPress } from "../shared/long-press.js";

const h = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
const SETTINGS_ROW = { readaloud: "extReadAloud", highlighter: "extHighlighter", darkmode: "extDarkMode", pip: "extPip", devtools: "extDevtools", adblock: "extAdblock", bandwidth: "extBandwidth", bell: "bellEnabled", music: "musicEnabled", vencord: "extVencord" };

// What each button does, and whether it shows as pressed.
const ACT = {
	readaloud: { run: (b) => readGate.toggle(b), pressed: () => readGate.reading(), name: () => (readGate.reading() ? "Read Aloud, reading" : "Read Aloud"), popup: "dialog" },
	highlighter: { run: () => { setPen(!penOn()); announce(penOn() ? "Highlighter on. Select text to highlight it." : "Highlighter paused."); }, pressed: () => penOn(), menu: () => [
		...(cardOk(active()) ? [["Make card from highlight", () => makeCard(active())]] : []),
		...(citeOk(active()) ? [["Cite this page", () => citePage(active())]] : []),
		["Clear highlights", () => { const r = clearHighlightsActive(); if (r && r.ok) announce("Highlights cleared."); }],
	] },
	darkmode: { run: () => { setDarkPaused(!darkPausedNow()); announce(darkPausedNow() ? "Dark Web Pages paused." : "Dark Web Pages on."); }, pressed: () => !darkPausedNow() },
	pip: { run: () => { const r = pipActive(); if (r && r.reason) announce(r.reason); setTimeout(paint, 400); }, pressed: () => pipOn() },
	devtools: { run: () => { if (!openDevtoolsActive()) announce("Open a website first, then DevTools."); } },
	adblock: { info: () => "Ad Blocker is on. It blocks ads and trackers on the sites you open.", popup: "menu" },
	bandwidth: { info: () => "Bandwidth Saver is on. It blocks autoplay video and heavy media.", popup: "menu" },
	bell: { info: () => { const n = bellNext(); return n ? "Next bell at " + n.text + (n.label ? ", " + n.label : "") + "." : "No more bells today."; }, popup: "menu" },
	vencord: { info: () => "Vencord (beta) is on. It runs in Discord tabs on the Standard engine. Reload a Discord tab after turning it on or off.", popup: "menu" },
	music: { run: () => musicCtl.toggle(), pressed: () => musicCtl.playing(), name: () => (musicCtl.playing() ? "Background music, playing" : "Background music") },
};

// Safari on iPhone and iPad never sends contextmenu for a long-press, so touch and pen get their own.
let pressedAt = 0;

let bar = null;
function makeButton(id, inIsland) {
	const x = EXTENSION_MAP[id];
	const a = ACT[id] || {};
	const b = h("button", inIsland ? "rv-isl-nav rv-extbtn" : "rv-nav rv-extbtn");
	b.type = "button";
	b.dataset.ext = id;
	b.innerHTML = x.icon + '<span class="rv-extbtn-label"></span>';
	b.querySelector(".rv-extbtn-label").textContent = x.label;
	if (a.popup) b.setAttribute("aria-haspopup", a.popup);
	b.addEventListener("click", () => {
		if (pressedAt && Date.now() - pressedAt < 800) { pressedAt = 0; return; } // the end of a long-press
		if (a.run) { a.run(b); paintButton(b); }
		else openMenu(b, id, false);
	});
	b.addEventListener("contextmenu", (e) => { e.preventDefault(); if (Date.now() - pressedAt < 800) return; openMenu(b, id, !e.clientX && !e.clientY); });
	longPress(b, () => { pressedAt = Date.now(); openMenu(b, id, false); });
	b.addEventListener("keydown", (e) => {
		if ((e.shiftKey && e.key === "F10") || e.key === "ContextMenu") { e.preventDefault(); openMenu(b, id, true); }
	});
	paintButton(b);
	return b;
}
function paintButton(b) {
	const id = b.dataset.ext;
	const a = ACT[id] || {};
	const name = a.name ? a.name() : EXTENSION_MAP[id].label;
	b.setAttribute("aria-label", name);
	b.title = name;
	if (a.pressed) b.setAttribute("aria-pressed", String(!!a.pressed())); else b.removeAttribute("aria-pressed");
	b.classList.toggle("rv-extbtn-busy", id === "readaloud" && readGate.reading());
}

// Roving tabindex: one stop for the whole group, arrows inside it.
const roving = new WeakSet();
function rove(group) {
	const items = () => [...group.querySelectorAll(".rv-extbtn")];
	const all = items();
	if (all.length && !all.some((b) => b.tabIndex === 0)) all.forEach((b, i) => { b.tabIndex = i === 0 ? 0 : -1; });
	if (roving.has(group)) return;
	roving.add(group);
	group.addEventListener("keydown", (e) => {
		const list = items();
		const i = list.indexOf(document.activeElement);
		if (i < 0) return;
		const j = e.key === "ArrowRight" ? i + 1 : e.key === "ArrowLeft" ? i - 1 : e.key === "Home" ? 0 : e.key === "End" ? list.length - 1 : null;
		if (j == null) return;
		e.preventDefault();
		const n = list[(j + list.length) % list.length];
		list.forEach((b) => { b.tabIndex = b === n ? 0 : -1; });
		n.focus();
	});
	group.addEventListener("focusin", (e) => { const b = e.target.closest(".rv-extbtn"); if (b) items().forEach((x) => { x.tabIndex = x === b ? 0 : -1; }); });
}

// The buttons that fit beside the address bar now; the rest are "overflow" for the extensions menu.
let overflow = [];
export const overflowExts = () => overflow.slice();
function render() {
	if (!bar) return;
	const ids = toolbarExts(get);
	const phone = innerWidth < 600;
	const n = phone ? ids.length : maxButtons(innerWidth);
	const shown = ids.slice(0, n);
	overflow = ids.slice(n);
	const keep = bar.contains(document.activeElement) ? document.activeElement.dataset.ext : "";
	const sig = shown.join(",");
	if (bar.dataset.sig !== sig) {
		bar.textContent = "";
		for (const id of shown) bar.append(makeButton(id, false));
		bar.dataset.sig = sig;
		rove(bar);
		const again = keep && bar.querySelector('[data-ext="' + keep + '"]');
		if (again) again.focus();
	}
	bar.hidden = !shown.length;
	paint();
}
export function paint() {
	for (const b of document.querySelectorAll(".rv-extbtn")) paintButton(b);
}

// --- the button menu -------------------------------------------------------------------------------
let menu = null, menuFrom = null;
function closeMenu(restore) {
	if (!menu) return;
	menu.remove();
	menu = null;
	if (menuFrom) { menuFrom.setAttribute("aria-expanded", "false"); if (restore && menuFrom.isConnected) menuFrom.focus(); }
	menuFrom = null;
}
function openMenu(anchor, id, kb) {
	closeMenu(false);
	const x = EXTENSION_MAP[id];
	const a = ACT[id] || {};
	menuFrom = anchor;
	menu = h("div", "rv-extmenu");
	menu.setAttribute("role", "menu");
	menu.setAttribute("aria-label", x.label);
	let infoId = "";
	if (a.info) { const p = h("p", "rv-extmenu-info", a.info()); p.setAttribute("role", "none"); p.id = infoId = "rv-extmenu-info"; menu.append(p); }
	const order = toolbarExts(get);
	const i = order.indexOf(id);
	const items = [
		...(typeof a.menu === "function" ? a.menu() : a.menu || []),
		...(i > 0 ? [["Move left", () => { const p = movePatch(id, -1, get); if (p) setMany(p); announce(x.label + " moved left."); }]] : []),
		...(i >= 0 && i < order.length - 1 ? [["Move right", () => { const p = movePatch(id, 1, get); if (p) setMany(p); announce(x.label + " moved right."); }]] : []),
		["Unpin from toolbar", () => { setMany(pinPatch(id, false, get)); announce(x.label + " is off the toolbar. Pin it again from the extensions menu."); }],
		["Turn off " + x.label, () => { set(x.key, false); announce(x.label + " is off."); }],
		["Settings for " + x.label, () => openSettings("#set=" + SETTINGS_ROW[id])],
	];
	for (const [label, run] of items) {
		const b = h("button", "rv-extmenu-item", label);
		b.type = "button";
		b.setAttribute("role", "menuitem");
		b.tabIndex = -1;
		b.addEventListener("click", () => { const from = menuFrom; closeMenu(false); run(); if (from && from.isConnected) from.focus(); else { const any = document.querySelector(".rv-extbtn"); if (any) any.focus(); } });
		menu.append(b);
	}
	document.body.append(menu);
	const r = anchor.getBoundingClientRect();
	const w = menu.offsetWidth;
	menu.style.left = Math.max(8, Math.min(r.left, innerWidth - w - 8)) + "px";
	if (r.top < innerHeight / 2) menu.style.top = r.bottom + 6 + "px"; else menu.style.bottom = innerHeight - r.top + 6 + "px";
	anchor.setAttribute("aria-expanded", "true");
	const list = () => [...menu.querySelectorAll('[role="menuitem"]')];
	menu.addEventListener("keydown", (e) => {
		const l = list();
		const k = l.indexOf(document.activeElement);
		if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); closeMenu(true); }
		else if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); l[(k + (e.key === "ArrowDown" ? 1 : -1) + l.length) % l.length].focus(); }
		else if (e.key === "Home" || e.key === "End") { e.preventDefault(); l[e.key === "Home" ? 0 : l.length - 1].focus(); }
		else if (e.key === "Tab") closeMenu(false);
	});
	const first = list()[0];
	// focus lands on the first item, so that is where a screen reader hears "Next bell at ..."
	if (first && infoId) first.setAttribute("aria-describedby", infoId);
	if (first) first.focus({ preventScroll: true });
	void kb;
}
document.addEventListener("pointerdown", (e) => { if (menu && !menu.contains(e.target) && !(menuFrom && menuFrom.contains(e.target))) closeMenu(false); }, true);

// The island shows the same buttons (as many as fit) in its bar.
export const extBarApi = {
	island(container) {
		const ids = toolbarExts(get).slice(0, Math.max(1, Math.min(3, maxButtons(innerWidth))));
		const sig = ids.join(",");
		if (container.dataset.sig !== sig) {
			container.textContent = "";
			for (const id of ids) container.append(makeButton(id, true));
			container.dataset.sig = sig;
			rove(container);
		} else for (const b of container.querySelectorAll(".rv-extbtn")) paintButton(b);
	},
	paint,
};

export function initExtBar() {
	bar = document.getElementById("rv-extbar");
	render();
	subscribe((_s, d) => { if (!d || !d.keys || d.keys.some((k) => /^ext|^bellEnabled$|^musicEnabled$/.test(k))) render(); else paint(); });
	addEventListener("resize", () => { closeMenu(false); render(); });
	readGate.onChange(paint);
	setInterval(() => { if (document.querySelector('.rv-extbtn[data-ext="pip"], .rv-extbtn[data-ext="music"]')) paint(); }, 2000);
}
